import React, { useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { BtnUi, Icon } from "~/components";
import styles from "./DialogUi.module.css";
import classNames from "classnames";

export const DialogUi = ({
  header,
  body,
  footer,
  onClose,
  modalStyle,
  modalHeaderStyle,
  closeButtonStyle,
  modalFooterStyle,
  modalBodyStyle,
  disableOverlayClose = false,
  disableEscapeClose = false,
  "data-testid": testId = "dialog",
  ...props
}) => {
  // Close on overlay click
  const closeOnOverlay = useCallback(
    ({ target }) => {
      if (disableOverlayClose) {
        return;
      }

      if (target.className === styles.overlay) {
        onClose();
      }
    },
    [disableOverlayClose, onClose],
  );

  // Close on Esc
  useEffect(() => {
    if (disableEscapeClose) {
      return undefined;
    }

    const onEscape = ({ key }) => key === "Escape" && onClose();
    window.addEventListener("keyup", onEscape);

    return () => window.removeEventListener("keyup", onEscape);
  }, [disableEscapeClose, onClose]);

  return createPortal(
    <div className={styles.overlay} data-testid={`${testId}-overlay`} {...props} onClick={closeOnOverlay}>
      <section className={classNames(styles.dialog, modalStyle)} data-testid={testId} role="dialog" aria-modal="true">
        <div className={classNames(styles.dialogHeader, modalHeaderStyle)}>
          <BtnUi
            data-testid="dialog-close"
            aria-label="Close dialog"
            variant="icon"
            className={classNames(styles.close, closeButtonStyle)}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}
          >
            <Icon name="close" className={styles.closeIcon} />
          </BtnUi>
          {header}
        </div>
        <div className={classNames(styles.dialogBody, modalBodyStyle)}>
          {body}
        </div>
        <div className={classNames(styles.dialogFooter, modalFooterStyle)}>
          {footer}
        </div>
      </section>
    </div>,
    document.body,
  );
};
